/**
 * User consent utilities for managing privacy preferences (e.g. analytics tracking).
 *
 * Consent is stored as AWSJSON on the User model, decoupled from T&C acceptance.
 * Users can change consent at any time from the settings page.
 *
 * Structure: { "userTracking": true, "updatedAt": "ISO-8601 date" }
 */

export interface UserConsentData {
  userTracking: boolean;
  updatedAt: string; // ISO 8601
}

const DEFAULT_CONSENT: UserConsentData = {
  userTracking: false,
  updatedAt: "",
};

/** Parse AWSJSON consent string. Defaults to tracking disabled. */
export function parseUserConsent(raw: string | null | undefined): UserConsentData {
  if (!raw) return { ...DEFAULT_CONSENT };
  try {
    const parsed = JSON.parse(raw);
    return {
      userTracking: parsed?.userTracking === true,
      updatedAt: parsed?.updatedAt ?? "",
    };
  } catch {
    return { ...DEFAULT_CONSENT };
  }
}

/** Build an updated consent AWSJSON string with a new timestamp. */
export function updateConsent(
  existing: UserConsentData,
  key: keyof Omit<UserConsentData, "updatedAt">,
  value: boolean
): string {
  const updated: UserConsentData = {
    ...existing,
    [key]: value,
    updatedAt: new Date().toISOString(),
  };
  return JSON.stringify(updated);
}

/** Build a full consent AWSJSON string (used during initial T&C acceptance). */
export function buildInitialConsent(userTracking: boolean): string {
  const consent: UserConsentData = {
    userTracking,
    updatedAt: new Date().toISOString(),
  };
  return JSON.stringify(consent);
}

/** Shorthand: does the user consent to analytics tracking? */
export function hasTrackingConsent(raw: string | null | undefined): boolean {
  return parseUserConsent(raw).userTracking;
}
